import { useEffect } from 'react'
import { useChatStore } from '@/store/chatStore'

// ─────────────────────────────────────────────────────────────────────────────
// ChatBubble
// Floating action button in bottom-right corner.
// Only rendered in embed (iFrame) mode.
// Click opens/closes the ChatPanel.
// Posts postMessage to parent on every state change so loader.js can resize
// the iframe — keeping it bubble-sized when closed and full-sized when open.
// ─────────────────────────────────────────────────────────────────────────────
// Injected once — not inside the component to avoid re-render style conflicts
const BUBBLE_STYLES = `
  @keyframes bubblePulse {
    0%   { transform: scale(1);   opacity: 0.6; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  @keyframes bubbleWiggle {
    0%, 85%, 100% { transform: rotate(0deg); }
    88%           { transform: rotate(-12deg); }
    92%           { transform: rotate(12deg); }
    96%           { transform: rotate(-8deg); }
    99%           { transform: rotate(6deg); }
  }
  .bubble-pulse-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: var(--color-brand);
    animation: bubblePulse 2s ease-out infinite;
  }
  .bubble-pulse-ring-delay {
    animation-delay: 0.8s;
  }
  .bubble-icon-wiggle {
    animation: bubbleWiggle 4s ease-in-out infinite;
  }
`
export function ChatBubble() {
  const isPanelOpen = useChatStore((s) => s.isPanelOpen)
  const togglePanel = useChatStore((s) => s.togglePanel)

  useEffect(() => {
  // Inject styles once — guard against double injection in dev StrictMode
  if (document.getElementById('dirayah-bubble-styles')) return
  const tag = document.createElement('style')
  tag.id = 'dirayah-bubble-styles'
  tag.textContent = BUBBLE_STYLES
  document.head.appendChild(tag)
}, [])

  return (
    <>
      {/* Inject keyframes — scoped to widget iframe */}

      <button
        onClick={togglePanel}
        title={isPanelOpen ? 'Close chat' : 'Open chat'}
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'var(--color-brand)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          zIndex: 50,
          transition: 'transform 0.2s ease, background 0.2s ease',
          transform: isPanelOpen ? 'scale(0.95)' : 'scale(1)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--color-brand-hover)'
          e.currentTarget.style.transform = 'scale(1.08)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--color-brand)'
          e.currentTarget.style.transform = isPanelOpen ? 'scale(0.95)' : 'scale(1)'
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.94)')}
        onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
      >
        {/* Pulse rings — only when closed */}
        {!isPanelOpen && (
          <>
            <span className="bubble-pulse-ring" />
            <span className="bubble-pulse-ring bubble-pulse-ring-delay" />
          </>
        )}

        {/* Icon — wiggle when closed */}
        <span
          className={!isPanelOpen ? 'bubble-icon-wiggle' : ''}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}
        >
          {isPanelOpen ? (
            // Close X icon
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
              fill="white" style={{ width: 24, height: 24, transition: 'transform 0.3s ease', transform: 'rotate(0deg)' }}>
              <path fillRule="evenodd"
                d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                clipRule="evenodd" />
            </svg>
          ) : (
            // Chat icon
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
              fill="white" style={{ width: 24, height: 24 }}>
              <path fillRule="evenodd"
                d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z"
                clipRule="evenodd" />
            </svg>
          )}
        </span>

        {/* Badge — shows "1" when closed to create curiosity */}
        {!isPanelOpen && (
          <span
            style={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: '#ef4444',
              border: '2px solid white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: '700',
              color: 'white',
              fontFamily: 'sans-serif',
              zIndex: 2,
              lineHeight: 1,
            }}
          >
            1
          </span>
        )}
      </button>
    </>
  )
}